import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { MotionProvider } from "@/lib/motion";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Educators from "@/pages/Educators";
import Parents from "@/pages/Parents";
import Classes from "@/pages/Classes";
import Enrollment from "@/pages/Enrollment";
import Attendance from "@/pages/Attendance";
import Grades from "@/pages/Grades";
import Results from "@/pages/Results";
import Materials from "@/pages/Materials";
import Invoices from "@/pages/Invoices";
import Announcements from "@/pages/Announcements";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import PrintInvoice from "@/pages/PrintInvoice";
import ReportCard from "@/pages/ReportCard";
import DeveloperCredit from "@/components/DeveloperCredit";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "var(--bg)" }} role="status">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-100 border-t-teal-600" />
      <div className="text-sm text-slate-600 font-sans">Loading…</div>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <LangProvider>
      <ThemeProvider>
      <MotionProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/students" element={<Protected><Students /></Protected>} />
            <Route path="/educators" element={<Protected><Educators /></Protected>} />
            <Route path="/parents" element={<Protected><Parents /></Protected>} />
            <Route path="/classes" element={<Protected><Classes /></Protected>} />
            <Route path="/enrollment" element={<Protected><Enrollment /></Protected>} />
            <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
            <Route path="/grades" element={<Protected><Grades /></Protected>} />
            <Route path="/results" element={<Protected><Results /></Protected>} />
            <Route path="/materials" element={<Protected><Materials /></Protected>} />
            <Route path="/invoices" element={<Protected roles={["admin", "parent"]}><Invoices /></Protected>} />
            <Route path="/announcements" element={<Protected><Announcements /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/invoices/:id/print" element={<Protected roles={["admin", "parent"]}><PrintInvoice /></Protected>} />
            <Route path="/students/:sid/report" element={<Protected><ReportCard /></Protected>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <DeveloperCredit />
        </BrowserRouter>
      </AuthProvider>
      </MotionProvider>
      </ThemeProvider>
    </LangProvider>
  );
}
